import { FC, useCallback, useEffect, useState } from 'react';
import FilterDropdown from '../custom-input/FilterDropdown';
import ValidatedInput from '../custom-input/ValidatedInput';
import Loader from '../loader/Loader';
import Tooltip from '../tooltip/Tooltip';
import { EmbryoStraw, SpecimenOwnership, SpecimenWithOwnership } from '../../types/interfaces/specimen.interfaces';
import { Account, selectOption } from '../../types/interfaces';
import { getUsePendingSpecimensByCanisterId } from '../../api/specimensApi';
import { showToast } from '../../services/toast.service';
import { formatPositiveNumber } from '../../utils/commonUtils';
import { toastMessages } from '../../constants/errorMessages';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import './specimenTable.scss';

export type SpecimenCheckInQuantity = {
  specimenId: number;
  transactionLinkId: number;
  accountId: number;
  qtyInCanister: number;
  usedQty: number;
  lostQty: number;
  checkInQty: number;
  selected: boolean;
  account: Account;
  embryoCountPerStraw?: number;
  gender?: string;
  embryoStrawId?: number;
  embryoStrawCode?: string;
  embryoStrawNumber?: string;
};

type SpecimenQuantitiesCheckInProps = {
  canisterId?: string;
  specimenIdAutofill?: number;
  transactionId?: number;
  clearCheckInLocation: () => void;
  onChange: (e: SpecimenCheckInQuantity | null) => void;
};

export interface SpecimenWithOwnershipAndStraw extends SpecimenWithOwnership {
  embryoStraw: EmbryoStraw | null;
}

const SelectSpecimenCheckIn: FC<SpecimenQuantitiesCheckInProps> = ({
  canisterId,
  specimenIdAutofill,
  transactionId,
  clearCheckInLocation,
  onChange,
}): JSX.Element => {
  const [checkInSpecimens, setCheckInSpecimens] = useState<SpecimenWithOwnershipAndStraw[]>();
  const [quantities, setQuantities] = useState<SpecimenCheckInQuantity[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const initialState = { value: '', name: '' };
  const [checkInSpecimenOptions, setCheckInSpecimenOptions] = useState<selectOption[]>([initialState]);
  const [checkInSpecimenValue, setCheckInSpecimenValue] = useState<selectOption>(initialState);
  const [selectedSpecimenIndex, setSelectedSpecimenIndex] = useState<number>(-1);

  const isSpecimenSelected = (specimenId: number, specimenOwnership: SpecimenOwnership) => {
    return transactionId != undefined
      ? specimenId === specimenIdAutofill && specimenOwnership.transactionLinkId === transactionId
      : specimenId === specimenIdAutofill;
  };

  const handleGetPendingUseTransactions = useCallback(async () => {
    try {
      if (!canisterId) {
        setCheckInSpecimens([]);
        setQuantities([]);
        return;
      }
      setIsLoading(true);
      const { data: specimensWithUseTransactions } = await getUsePendingSpecimensByCanisterId(canisterId, {
        include: 'Animal, embryoStraws',
      });

      const flattenedSpecimens: SpecimenWithOwnershipAndStraw[] = specimensWithUseTransactions.flatMap(
        (specimen: SpecimenWithOwnership): SpecimenWithOwnershipAndStraw[] => {
          const isFemale = specimen?.animal?.gender?.toLowerCase() === 'female';

          if (isFemale) {
            if (specimen.embryoStraws && specimen.embryoStraws.length > 0) {
              return specimen.embryoStraws.map(straw => ({
                ...specimen,
                embryoStraw: straw,
              }));
            }
            return [];
          }

          return [{ ...specimen, embryoStraw: null }];
        },
      );

      flattenedSpecimens.sort((a, b) => b.specimenOwnership[0]?.transactionLinkId! - a.specimenOwnership[0]?.transactionLinkId!);

      setCheckInSpecimenOptions(
        flattenedSpecimens.map((specimen: any, index) => {
          return {
            name: `${specimen.animal?.code} - ${specimen.specimenOwnership[0].account?.name} - ${new Date(
              specimen.freezeDate.toString(),
            ).toLocaleDateString()} ${specimen.embryoStraw ? `(${specimen.embryoStraw.embryoStrawCode})` : ''}`,
            value: `${index}`,
          };
        }),
      );

      setCheckInSpecimens(flattenedSpecimens);

      const newQuantities = flattenedSpecimens.map((specimen: SpecimenWithOwnershipAndStraw) => {
        const isFemale = specimen.animal?.gender?.toLowerCase() === 'female';
        const embryoCountPerStraw = specimen.embryoStraw?.embryoCountPerStraw ?? 0;
        const ownershipQty = specimen.specimenOwnership?.[0]?.quantity ?? 0;
        const transactionLinkId = specimen.specimenOwnership?.[0]?.transactionLinkId;
        return {
          specimenId: specimen.specimenId,
          accountId: specimen.specimenOwnership[0].accountId,
          transactionLinkId: transactionLinkId!,
          qtyInCanister: isFemale ? embryoCountPerStraw : ownershipQty,
          usedQty: isFemale ? embryoCountPerStraw : 0,
          lostQty: 0,
          checkInQty: 0,
          selected: false,
          account: specimen.specimenOwnership[0].account,
          embryoStrawCode: specimen.embryoStraw?.embryoStrawCode ?? '-',
          embryoStrawNumber: specimen.embryoStraw?.embryoStrawNumber ?? '-',
          embryoStrawId: specimen.embryoStraw?.embryoStrawId ?? 0,
          embryoGrade: specimen.embryoStraw?.embryoGrade ?? null,
          embryoStage: specimen.embryoStraw?.embryoStage ?? null,
          gender: specimen.animal?.gender,
        };
      });

      setQuantities(newQuantities);
      setSelectedSpecimenIndex(-1);
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  }, [canisterId]);

  useEffect(() => {
    handleGetPendingUseTransactions();
  }, [handleGetPendingUseTransactions]);

  useEffect(() => {
    onChange(
      quantities.reduce((acc: SpecimenCheckInQuantity | null, q: SpecimenCheckInQuantity) => {
        return q.selected ? q : acc;
      }, null),
    );

    const specimenIndex = quantities.findIndex(
      (specimen: SpecimenCheckInQuantity) =>
        specimen.specimenId === specimenIdAutofill && specimen.transactionLinkId === transactionId,
    );

    if (specimenIndex != -1) {
      setCheckInSpecimenValue(checkInSpecimenOptions[specimenIndex]);
    } else {
      setCheckInSpecimenValue(initialState);
    }
  }, [quantities]);

  const getPlaceholder = (specimen: SpecimenCheckInQuantity, quantity: number): string => {
    return specimen?.selected ? (quantity > 0 ? quantity.toString() : '') : '-';
  };

  const handleOnChangeSpecimen = (e: selectOption) => {
    setCheckInSpecimenValue(e);
    clearCheckInLocation();
    setQuantities(
      quantities.map((quantity, index) => {
        return index === Number(e.value)
          ? { ...quantity, selected: true }
          : { ...quantity, selected: false, usedQty: 0, lostQty: 0, checkInQty: 0 };
      }),
    );

    setSelectedSpecimenIndex(Number(e.value) ?? -1);
  };

  return (
    <div className="select-specimen-checkin">
      <div className="desk-select-specimen">
        <div className="specimen-table">
          <table>
            <thead>
              <tr>
                <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                <th>{TABLE_HEADER_CONSTANTS.LINK_ID}</th>
                <th>{TABLE_HEADER_CONSTANTS.ITEMS}</th>
                <th>{TABLE_HEADER_CONSTANTS.ACCOUNT}</th>
                <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                <th>{TABLE_HEADER_CONSTANTS.USED_QTY}</th>
                <th>{TABLE_HEADER_CONSTANTS.UNUSABLE_QTY}</th>
                <th>{TABLE_HEADER_CONSTANTS.CHECK_IN_QTY}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={10}>
                    <Loader addedSpace loaderSize="small" />
                  </td>
                </tr>
              )}
              {!isLoading && checkInSpecimens?.length === 0 && (
                <tr>
                  <td colSpan={7}>{LABEL_CONSTANTS.NO_SPECIMENS_AVAILABLE_FOR_CHECK_IN}</td>
                </tr>
              )}
              {checkInSpecimens?.map((spec, index) => {
                return (
                  <tr
                    key={'specimen-checkin-row:' + spec.specimenId + '-index:' + index}
                    className={
                      spec.specimenOwnership[0].account.outstandingBalanceCents != undefined &&
                      spec.specimenOwnership[0].account.penaltyCents != undefined &&
                      (spec.specimenOwnership[0].account.outstandingBalanceCents > 0 ||
                        spec.specimenOwnership[0].account.penaltyCents > 0)
                        ? 'warning'
                        : ''
                    }>
                    <td>
                      <input
                        type="radio"
                        name="selected-checkin-specimen-row"
                        checked={quantities[index]?.selected ?? false}
                        onChange={() => {
                          clearCheckInLocation();
                          setSelectedSpecimenIndex(index);
                          setCheckInSpecimenValue(checkInSpecimenOptions[index]);

                          setQuantities(prev =>
                            prev.map((quantity: SpecimenCheckInQuantity, indx: number) => {
                              if (indx === index) {
                                const spec = checkInSpecimens?.[indx];
                                const isFemale = spec?.animal?.gender?.toLowerCase()?.trim() === 'female';
                                const embryoCountPerStraw = spec?.embryoStraw?.embryoCountPerStraw ?? 0;
                                const ownershipQty = spec?.specimenOwnership?.[0]?.quantity ?? 0;
                                const qtyInCanister = isFemale ? embryoCountPerStraw : ownershipQty;

                                return {
                                  ...quantity,
                                  specimenId: spec?.specimenId ?? quantity.specimenId,
                                  transactionLinkId:
                                    spec?.specimenOwnership?.[0]?.transactionLinkId ?? quantity.transactionLinkId,
                                  qtyInCanister,
                                  usedQty: isFemale ? qtyInCanister : 0,
                                  lostQty: 0,
                                  checkInQty: 0,
                                  selected: true,
                                  account: spec?.specimenOwnership?.[0]?.account ?? quantity.account,
                                  embryoStrawCode: spec?.embryoStraw?.embryoStrawCode ?? quantity.embryoStrawCode,
                                  embryoStrawNumber: spec?.embryoStraw?.embryoStrawNumber ?? quantity.embryoStrawNumber,
                                  embryoStrawId: spec?.embryoStraw?.embryoStrawId ?? quantity.embryoStrawId,
                                  gender: spec?.animal?.gender ?? quantity.gender,
                                } as SpecimenCheckInQuantity;
                              } else {
                                return { ...quantity, selected: false, usedQty: 0, lostQty: 0, checkInQty: 0 };
                              }
                            }),
                          );
                        }}
                      />
                    </td>
                    <td>{spec.specimenOwnership[0].transactionLinkId}</td>
                    <td>
                      {spec.animal?.code} - {new Date(spec.freezeDate.toString() as string).toLocaleDateString()}
                    </td>
                    <td>
                      {spec.specimenOwnership[0].account?.name}{' '}
                      <label className="account-due-payment">
                        {spec.specimenOwnership[0].account.outstandingBalanceCents != undefined &&
                        spec.specimenOwnership[0].account.penaltyCents != undefined &&
                        (spec.specimenOwnership[0].account.outstandingBalanceCents > 0 ||
                          spec.specimenOwnership[0].account.penaltyCents > 0)
                          ? '(Due Payment)'
                          : ''}
                      </label>
                    </td>
                    <td>
                      {spec.embryoStraw?.embryoStrawCode ?? '-'}

                      {spec.embryoStraw && (
                        <Tooltip>
                          {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${spec.embryoStraw?.embryoGrade || 'NA'}`} <br />
                          {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${spec.embryoStraw?.embryoStage || 'NA'}`}
                        </Tooltip>
                      )}
                    </td>
                    <td>{spec.embryoStraw?.embryoStrawNumber ?? '-'}</td>
                    <td>{quantities[index]?.qtyInCanister}</td>
                    <td>
                      <input
                        type="text"
                        placeholder="0"
                        value={getPlaceholder(quantities[index], quantities[index].usedQty)}
                        disabled={!quantities[index]?.selected || quantities[index]?.gender?.toLowerCase()?.trim() === 'female'}
                        onChange={e => {
                          const count = formatPositiveNumber(e.target.value);
                          let newQuantities = [...quantities];
                          newQuantities[index] = {
                            ...quantities[index],
                            usedQty: count,
                          };
                          setQuantities(newQuantities);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="0"
                        value={getPlaceholder(quantities[index], quantities[index].lostQty)}
                        disabled={!quantities[index]?.selected || quantities[index]?.gender?.toLowerCase()?.trim() === 'female'}
                        onChange={e => {
                          const count = formatPositiveNumber(e.target.value);
                          let newQuantities = [...quantities];
                          newQuantities[index] = {
                            ...quantities[index],
                            lostQty: count,
                          };
                          setQuantities(newQuantities);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="0"
                        value={getPlaceholder(quantities[index], quantities[index].checkInQty)}
                        disabled={!quantities[index]?.selected || quantities[index]?.gender?.toLowerCase()?.trim() === 'female'}
                        onChange={e => {
                          const count = formatPositiveNumber(e.target.value);
                          let newQuantities = [...quantities];
                          newQuantities[index] = {
                            ...quantities[index],
                            checkInQty: count,
                          };
                          setQuantities(newQuantities);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={10}>&nbsp;</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View */}
      <div className="xs-select-specimen">
        {isLoading ? (
          <Loader addedSpace loaderSize="small" />
        ) : checkInSpecimens?.length === 0 ? (
          <label className="no-specimen">{LABEL_CONSTANTS.NO_SPECIMENS_AVAILABLE_FOR_CHECK_IN}</label>
        ) : (
          <>
            <div className="form-row">
              <label>{TABLE_HEADER_CONSTANTS.ITEMS}: </label>
              <div className="input-container wide-input-container">
                <FilterDropdown
                  placeholder={'Select One'}
                  options={checkInSpecimenOptions}
                  value={checkInSpecimenValue}
                  onChange={e => handleOnChangeSpecimen(e)}
                />
              </div>
            </div>
            {quantities[selectedSpecimenIndex] && (
              <>
                <div className="form-row">
                  <label>{TABLE_HEADER_CONSTANTS.ACCOUNT}:</label>
                  <div className="input-container">
                    <label>
                      {quantities[selectedSpecimenIndex]?.account.name ?? '-'}
                      {quantities[selectedSpecimenIndex].account?.outstandingBalanceCents === 0 &&
                      quantities[selectedSpecimenIndex].account?.penaltyCents === 0 ? (
                        <></>
                      ) : (
                        <label className="account-due-payment">{LABEL_CONSTANTS.DUE_PAYMENT}</label>
                      )}
                    </label>
                  </div>
                </div>
                <div className="form-row">
                  <label>{LABEL_CONSTANTS.TRANSACTION_LINK_ID}:</label>
                  <div className="input-container">
                    <label>{quantities[selectedSpecimenIndex]?.transactionLinkId ?? '-'}</label>
                  </div>
                </div>

                <div className="form-row">
                  <label>{TABLE_HEADER_CONSTANTS.STRAW_LOT}:</label>
                  <div className="input-container">
                    <label>{quantities[selectedSpecimenIndex]?.embryoStrawCode ?? '-'}</label>
                  </div>
                </div>

                <div className="form-row">
                  <label>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}:</label>
                  <div className="input-container">
                    <label>{quantities[selectedSpecimenIndex]?.embryoStrawNumber ?? '-'}</label>
                  </div>
                </div>
              </>
            )}

            <div className="form-row">
              <label>{TABLE_HEADER_CONSTANTS.QUANTITY}:</label>
              <div className="input-container">
                <label>{quantities[selectedSpecimenIndex]?.qtyInCanister ?? '-'}</label>
              </div>
            </div>
            <div className="form-row">
              <ValidatedInput
                label="Used:"
                type="number"
                validatedStateForAutoFill={{ value: (quantities[selectedSpecimenIndex]?.usedQty ?? '').toString(), valid: true }}
                setValidatedState={e => {
                  let newQuantities = [...quantities];
                  newQuantities[selectedSpecimenIndex] = {
                    ...quantities[selectedSpecimenIndex],
                    usedQty: Number(e.value),
                  };
                  setQuantities(newQuantities);
                }}
                validators={[]}
              />
            </div>
            <div className="form-row">
              <ValidatedInput
                label="Lost:"
                type="number"
                validatedStateForAutoFill={{ value: (quantities[selectedSpecimenIndex]?.lostQty ?? '').toString(), valid: true }}
                setValidatedState={e => {
                  let newQuantities = [...quantities];
                  newQuantities[selectedSpecimenIndex] = {
                    ...quantities[selectedSpecimenIndex],
                    lostQty: Number(e.value),
                  };
                  setQuantities(newQuantities);
                }}
                validators={[]}
              />
            </div>
            <div className="form-row">
              <ValidatedInput
                label="Check In Qty:"
                type="number"
                validatedStateForAutoFill={{
                  value: (quantities[selectedSpecimenIndex]?.checkInQty ?? '').toString(),
                  valid: true,
                }}
                setValidatedState={e => {
                  let newQuantities = [...quantities];
                  newQuantities[selectedSpecimenIndex] = {
                    ...quantities[selectedSpecimenIndex],
                    checkInQty: Number(e.value),
                  };
                  setQuantities(newQuantities);
                }}
                validators={[]}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SelectSpecimenCheckIn;
